import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Loader2 } from "lucide-react";
import { WHATSAPP_URL } from "../data/tour";
import { SectionHead, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-b border-vine bg-transparent py-3 text-sm text-cream placeholder:text-sage/50 focus:border-marigold focus:outline-none transition-colors duration-300";

export const LeadForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", package: "kochi-to-kochi", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      if (window.fbq) window.fbq("track", "Lead");
      toast.success("Enquiry received! We'll reach out within 24 hours.");
      setForm({ name: "", phone: "", email: "", package: "kochi-to-kochi", message: "" });
    } catch {
      toast.error("Something went wrong — please WhatsApp us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="border-y border-vine bg-moss/40 py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <div>
          <SectionHead index="04" overline="Reserve Your Seat" title="Don't just dream, travel." />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-sage">
              Allow us to call you back! Drop your details and a TrekPanda trip expert will reach
              out with the full trip brief, payment link and departure checklist. Prefer instant answers?
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leadform-whatsapp-link"
              className="mt-8 inline-flex items-center gap-3 border border-marigold px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] text-marigold transition-colors duration-300 hover:bg-marigold hover:text-jungle"
            >
              <MessageCircle size={15} /> WhatsApp 95297 17726
            </a>
            <div className="mt-12 space-y-2 text-xs uppercase tracking-[0.2em] text-sage">
              <p>Trains — Every Friday, ex-Pune &amp; Mumbai</p>
              <p>Flights — 27th Sept + Every Sunday in Dec</p>
              <p>Kochi Departures — Every Sunday</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form onSubmit={submit} data-testid="lead-form" className="border border-vine bg-jungle p-8 lg:p-10">
            <div className="space-y-7">
              <div>
                <label htmlFor="lead-name" className="text-[11px] uppercase tracking-[0.25em] text-sage">Full Name</label>
                <input id="lead-name" data-testid="lead-name-input" required value={form.name} onChange={set("name")} placeholder="Aarav Sharma" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-phone" className="text-[11px] uppercase tracking-[0.25em] text-sage">Phone / WhatsApp</label>
                <input id="lead-phone" data-testid="lead-phone-input" required value={form.phone} onChange={set("phone")} placeholder="98220 12345" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-email" className="text-[11px] uppercase tracking-[0.25em] text-sage">Email</label>
                <input id="lead-email" type="email" data-testid="lead-email-input" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="lead-package" className="text-[11px] uppercase tracking-[0.25em] text-sage">Preferred Package</label>
                <select
                  id="lead-package"
                  data-testid="lead-package-select"
                  value={form.package}
                  onChange={set("package")}
                  className="w-full border-b border-vine bg-jungle py-3 text-sm text-cream focus:border-marigold focus:outline-none"
                >
                  <option value="kochi-to-kochi">Kochi to Kochi — ₹21,000 + 5% GST</option>
                  <option value="flight">Flight Package — ₹34,000 + taxes</option>
                  <option value="train">Train Package — ₹23,000 + taxes</option>
                </select>
              </div>
              <div>
                <label htmlFor="lead-message" className="text-[11px] uppercase tracking-[0.25em] text-sage">Message (optional)</label>
                <textarea id="lead-message" data-testid="lead-message-input" rows={3} value={form.message} onChange={set("message")} placeholder="Travelling with 3 friends, prefer 27 Sept flight…" className={`${inputCls} resize-none`} />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="lead-form-submit"
              className="mt-10 flex w-full items-center justify-center gap-2 bg-marigold py-4 text-xs font-bold uppercase tracking-[0.25em] text-jungle transition-colors duration-300 hover:bg-marigold-hover disabled:opacity-60"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Sending…" : "Send Enquiry"}
            </button>
            <p className="mt-4 text-center text-[11px] text-sage/70">No payment now — we call you first.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
